/**
 * Integration Hub API client.
 * Used for domain profiles, intents, FAQs, bots, etc.
 * Base URL: config.hubServer
 * Auth: the signed-in user's SSO bearer (getAuthedFetch). The hub accepts Bearer JWTs only — X-API-Key was
 * removed hub-side on 2026-07-03, so none is sent.
 */
import { botConfigDeletePath, botConfigListPath, botPatchPath } from '../lib/systemScope.ts';
import type { BotRef, BotSystem } from '../lib/botNav.ts';
import { config } from '../config';
import type { ShadowClass } from '../lib/shadowView';
import type { EmbeddingProfileRow } from '../lib/embeddingProfilesView';
import { getAuthedFetch } from './authBridge';
import { mergeRequestHeaders } from '../lib/requestHeaders';

// Every request goes through the chatbot_sso SDK's authenticated fetch (Bearer
// access_token + 401-refresh) via authBridge — the hub accepts Bearer JWTs only (X-API-Key removed 2026-07-03).
// The `token` param is retained for source
// compatibility with the ported pages but ignored — the SSO token is attached
// by authBridge regardless.
/** A failed read that carries the HTTP status, for pages that word a refusal (403 → "global admins only"). */
export class HubWriteError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string) {
    super(body);
    this.status = status;
    this.body = body;
  }
}

async function hubFetch(
  path: string,
  options: RequestInit,
  _token?: string | null,
): Promise<Response> {
  const url = `${config.hubServer.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = mergeRequestHeaders(
    { 'Content-Type': 'application/json' },
    options.headers,
  );
  const doFetch = getAuthedFetch() ?? fetch;
  return doFetch(url, { ...options, headers });
}

/**
 * Multipart upload: no JSON Content-Type — the browser sets multipart/form-data with its boundary. Array fields go
 * as `name[]` so the hub's multer (append-field) always builds an array, even for one value (`tags` is @IsArray).
 */
async function hubFetchForm(
  path: string,
  method: 'POST' | 'PUT',
  fields: Record<string, string | string[]>,
  file: File,
): Promise<Response> {
  const fd = new FormData();
  for (const [k, v] of Object.entries(fields)) {
    if (Array.isArray(v)) v.forEach((item) => fd.append(`${k}[]`, item));
    else fd.append(k, v);
  }
  fd.append('file', file);
  const url = `${config.hubServer.replace(/\/$/, '')}${path}`;
  const doFetch = getAuthedFetch() ?? fetch;
  return doFetch(url, { method, body: fd });
}

function knowledgePath(system: string, botId: string, rest: string): string {
  return `/api/knowledge/${encodeURIComponent(system)}/${encodeURIComponent(botId)}/${rest}`;
}

// --- Domain Profiles ---
export interface DomainProfile {
  id: string;
  domain_id: string;
  label: Record<string, string>;
  example_phrases: string[];
  is_system?: boolean;
  is_default?: boolean;
  sort_order?: number;
  is_active?: boolean;
}

export interface CreateDomainProfileDto {
  domain_id: string;
  label: Record<string, string>;
  example_phrases: string[];
  is_system?: boolean;
  is_default?: boolean;
  sort_order?: number;
  is_active?: boolean;
}

export interface UpdateDomainProfileDto {
  label?: Record<string, string>;
  example_phrases?: string[];
  sort_order?: number;
  is_default?: boolean;
  is_active?: boolean;
}

// --- Generic Intents ---
export interface GenericIntent {
  code: string;
  name: string;
  description: string;
  isActive?: boolean;
  triggerPatterns?: string[];
  examplePhrases?: string[];
  /** Platform-default reply (config-cascade bottom tier). */
  defaultReply?: { mn: string; en?: string };
}

export interface CreateGenericIntentDto {
  code: string;
  name: string;
  description: string;
  isActive?: boolean;
  triggerPatterns?: string[];
  examplePhrases?: string[];
}

export interface GenericIntentSyncResult {
  synced: number;
  results: Array<{
    code: string;
    ok: boolean;
    upserted?: number;
    removedStale?: number;
    error?: string;
  }>;
}

export interface UpdateGenericIntentDto {
  name?: string;
  description?: string;
  isActive?: boolean;
  triggerPatterns?: string[];
  examplePhrases?: string[];
  defaultReply?: { mn: string; en?: string } | null;
}

/** Per-bot extension row (config cascade tier 2): overrides the platform
 *  defaultReply, or points at a bot-side block/workflow, or disables the
 *  generic for this bot. */
export interface BotGenericIntentConfig {
  id?: string;
  botId: string;
  code: string;
  enabled: boolean;
  replyConfig?: { mn: string; en?: string } | null;
  blockRef?: string | null;
  workflowId?: string | null;
}

export interface UpsertBotGenericIntentConfigDto {
  system: string; // A1: 'chatbot' | 'chatbot_v2'
  botId: string;
  code: string;
  enabled?: boolean;
  replyConfig?: { mn: string; en?: string } | null;
  blockRef?: string | null;
  workflowId?: string | null;
}

// --- Intents (per bot) ---
export interface Intent {
  id: string;
  system: string;
  botId: string;
  intentCode: string;
  domain?: string;
  name: string;
  description?: string;
  workflowId?: string;
  confidenceThreshold?: number;
}

export interface CreateIntentDto {
  system: string;
  botId: string;
  intentCode: string;
  domain?: string;
  name: string;
  description?: string;
  workflowId?: string;
  confidenceThreshold?: number;
}

export interface UpdateIntentDto {
  /** Plan C1: On-match action (v2 intents); null clears it. */
  onMatch?: OnMatch | null;
  domain?: string;
  name?: string;
  description?: string;
  workflowId?: string;
  confidenceThreshold?: number;
}

export interface IntentText {
  id: string;
  intentId: string;
  text: string;
  pointId: string | null;
  syncedToVector: boolean;
  /** rasa = synced from chatbot (read-only), operator = hand-written, review = approved real message. */
  source?: 'rasa' | 'operator' | 'review';
  archivedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** One row of GET /api/intents/bot/:botId/summary (plan B1). */
export interface IntentSummary {
  id: string;
  intentCode: string;
  name: string;
  description: string | null;
  domain: string | null;
  workflowId: string | null;
  confidenceThreshold: number | null;
  kind: string | null;
  templateKey: string | null;
  legacyId: string | null;
  /** Synced from chatbot (only the v1 sync sets `kind`). */
  synced: boolean;
  counts: { rasa: number; operator: number; review: number };
  /** Live phrases not yet indexed. */
  unsynced: number;
  /** Plan C1: what hub tells v2 on a match (v2 bots). */
  onMatch?: OnMatch | null;
}

export type { OnMatch, V2Channel, BlockRef } from '../lib/onMatchView';
import type { OnMatch } from '../lib/onMatchView';
import type { EvalSummary, PolicyFields } from '../lib/moderationView';

/** One v2 block for the On-match picker (GET /api/intents/bot/:botId/v2-blocks). */
export interface V2Block {
  id: number;
  name: string;
  channel: string;
  folder: string | null;
  general: boolean;
  greeting: boolean;
}

export interface IntentConflict {
  text: string;
  intents: { id: string; intentCode: string; name: string }[];
}

export interface ImportStatus {
  state: string;
  status: string;
  lastSyncedAt: string | null;
  summary: unknown;
}

export interface IntentSyncStatus {
  total: number;
  synced: number;
  pending: number;
}

// --- Bot ---
export interface Bot {
  id: string;
  system: string;
  botId: string;
  name: string;
  enabledDomains?: string[] | null;
}

export interface LlmCacheStatus {
  enabled: boolean;
  cacheEnabled?: boolean | null;
  responseCache: { enabled: boolean };
  retrievalCache: { enabled: boolean };
  semanticCache: { enabled: boolean };
  config?: {
    responseTtlSec?: number;
    retrievalTtlSec?: number;
    semanticThreshold?: number;
  };
}

export interface SemanticCacheEntry {
  id: string;
  query: string;
  response: string;
  createdAt: string;
}

export interface LlmCacheStats {
  semantic: number;
  response: number;
  retrieval: number;
}

export interface RedisCacheEntry {
  key: string;
  value: string | unknown;
}

// --- LLM Token Usage ---
export interface LlmTokenUsageEntry {
  id: number;
  system: string;
  botId: string;
  recordedAt: string;
  requestTokens: number;
  responseTokens: number;
  totalTokens: number | null;
  model: string | null;
  /** vllm | openai | anthropic | azure */
  provider: string;
  /** generate | rerank | embed | chat */
  callType: string;
  source: string;
}

/** Bot wallet (LLM usage phase 1a). */
export interface WalletTx {
  id: number;
  type: 'topup' | 'adjust' | 'deduct';
  amountMnt: number;
  balanceAfterMnt: number;
  ref: string;
  note: string | null;
  actor: string | null;
  usageId: number | null;
  createdAt: string;
}
export interface BotWalletView {
  balanceMnt: number;
  transactions: WalletTx[];
}
export type WalletMovementResult =
  | { ok: true; balanceMnt: number; duplicate: boolean }
  | { ok: false; status: number; message: string };

export interface LlmUsageSummaryRow {
  system: string;
  botId: string;
  date: string;
  requestTokens: number;
  responseTokens: number;
  totalTokens: number;
  count: number;
}

// --- LLM Pool Admin (credential pools + pool models; flattened endpoints for monitoring) ---
export interface HubLlmPoolModel {
  id: string;
  poolId: string;
  modelKey: string;
  displayName: string | null;
  endpointKey: string | null;
  enabled: boolean;
}

export interface HubLlmPool {
  id: string;
  key: string;
  name: string;
  description: string | null;
  provider: string;
  apiKey: string | null;
  endpoints: Record<string, string>;
  providerConfig: Record<string, unknown>;
  enabled: boolean;
  draining: boolean;
  models: HubLlmPoolModel[];
  createdAt: string;
  updatedAt: string;
}

export interface LlmPoolPolicy {
  id: string;
  name: string;
  system: string | null;
  botId: string | null;
  strategy: string;
  fallbackProviders: string | null;
  maxRetries: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LlmPoolUsageCounterRow {
  system: string;
  botId: string;
  provider: string;
  promptTokens: number;
  completionTokens: number;
  requestCount: number;
  successCount: number;
}

// --- Agent router (POST /api/agent/route; hub stores session only in Redis) ---
export interface AgentRouteDto {
  message: string;
  userId?: string;
  system?: string;
  botId?: string;
  channel?: string;
  pageId?: string;
}

export interface AgentRouteResult {
  response: string;
}

export interface AgentRuntimeRouteDto extends AgentRouteDto {
  sessionSnapshot?: Record<string, unknown>;
}

export interface AgentRuntimeRouteResult extends AgentRouteResult {
  trace?: unknown;
}

// --- Agent planner (profiles CRUD + process) ---
export interface AgentPlannerProfile {
  id: string;
  name: string;
  system: string;
  botId: string;
  enabled: boolean;
  systemPromptOverride: string | null;
  maxLoopIterations: number | null;
  toolRankTopK: number | null;
  enableToolRanking: boolean | null;
  preRetrieveRag: boolean | null;
  minConfidence: number | null;
  includeBuiltinTools: boolean;
  appendIntentCapabilities: boolean | null;
  appendToolCapabilities: boolean | null;
  appendServiceCapabilities: boolean | null;
  appendRagGuidance: boolean | null;
  createdAt: string;
  updatedAt: string;
}

export type AgentPlannerResponseKind =
  | 'ask_user'
  | 'respond'
  | 'error'
  | 'max_iterations'
  | 'low_confidence';

export interface AgentPlannerProcessResult {
  response: string;
  kind: AgentPlannerResponseKind;
  plan?: unknown;
  session?: unknown;
}

// --- Agent workflows (Hub CRUD + publish; draftGraph / publishedGraph JSON) ---
export interface AgentWorkflow {
  id: string;
  system: string;
  botId: string;
  name: string;
  description: string | null;
  draftVersion: number;
  publishedVersion: number;
  draftGraph: Record<string, unknown>;
  publishedGraph: Record<string, unknown> | null;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AgentWorkflowNodeTypeDef {
  type: string;
  label: string;
  category: string;
  description?: string;
  entrySlots?: string[];
  outputSlots?: string[];
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  configSchema?: Record<string, unknown>;
  uiSchema?: Record<string, unknown>;
}

export interface AgentWorkflowEdgeTypeDef {
  type: string;
  label: string;
  description?: string;
  sourceSlots?: string[];
  targetSlots?: string[];
  configSchema?: Record<string, unknown>;
}

export interface CreateAgentWorkflowDto {
  system: string;
  botId: string;
  name: string;
  description?: string;
  flowType?: string;
  draftGraph?: Record<string, unknown>;
}

export interface UpdateAgentWorkflowDto {
  name?: string;
  description?: string;
  draftGraph?: Record<string, unknown>;
  enabled?: boolean;
  flowType?: string;
}

export interface PublishAgentWorkflowDto {
  note?: string;
}

export interface CompileAgentWorkflowResult {
  workflowId: string;
  flowType: string;
  compiledGraph: Record<string, unknown>;
  report: {
    valid: boolean;
    nodeCount: number;
    edgeCount: number;
    entryNodeId?: string;
  };
}

export interface InferenceGenerateUsage {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
}

/** POST /api/inference/generate — pool mode uses LlmPoolService (pool dispatch). */
export interface InferenceGenerateResult {
  /** Legacy: full assistant payload (`result ?? thinking` from pool). */
  text: string;
  /** Final answer when upstream splits reasoning vs answer; may be omitted on older hubs. */
  result?: string;
  /** Chain-of-thought / reasoning when separable (vLLM, part arrays, LangChain think tags). */
  thinking?: string;
  usage?: InferenceGenerateUsage;
  mode: string;
  adapter?: 'http' | 'langchain' | 'genai-http' | 'genai-sdk';
  apiStyle?: string;
  /** Echoed when request sent `transport` override (incl. stream). */
  transport?: string;
  execution?: {
    adapter?: string;
    /** Effective hint after pool row + optional request override. */
    transportHint?: string | null;
    apiSurface?: string;
    poolChatOutputType?: string;
  };
  /** Present for pool-backed responses: route target that served the request */
  provider?: string;
  model?: string;
  routeTargetId?: string;
}

export interface InferenceToolInput {
  name: string;
  description?: string;
  inputSchema?: unknown;
}

// --- LLM providers: the operator's view of the catalog ---
//
// Replaces the old `/cloud` page, which asked HUB which provider keys were set. That answer
// was structurally wrong: keys live in tool-service's environment and hub never holds one,
// so the page could report `OPENAI: configured` while every bot silently fell back to vLLM.
// A green tick that is wrong is worse than no page.

/**
 * Two independent axes, never collapsed into one boolean — because the fix differs:
 *   available   — active here AND the key exists in tool-service
 *   key_missing — active here, but the env var is not set THERE (fix on the tool-service host)
 *   disabled    — switched off HERE (fix on this screen)
 *   unknown     — tool-service unreachable, so the check did not run (not a negative answer)
 */
export type ProviderStatus = 'available' | 'key_missing' | 'disabled' | 'unknown';

export interface OperatorLlmProvider {
  id: string;
  vendor: string;
  model: string;
  label: string;
  /** Published ₮ per 1k tokens — what a bot owner is charged. */
  inputPricePer1kMnt: number;
  outputPricePer1kMnt: number;
  /** What the vendor bills us, USD per 1k. Cost side of the margin; never shown to owners. */
  inputCostPer1kUsd: number;
  outputCostPer1kUsd: number;
  toolsCapable: boolean;
  /** NAME of an env var in tool-service, never a key value. null = use the vendor default. */
  keyRef: string | null;
  isActive: boolean;
  isDefault: boolean;
  status: ProviderStatus;
  effectiveKeyRef: string | null;
}

export interface CreateLlmProviderDto {
  id: string;
  vendor: string;
  model: string;
  label: string;
  inputPricePer1kMnt?: number;
  outputPricePer1kMnt?: number;
  inputCostPer1kUsd?: number;
  outputCostPer1kUsd?: number;
  toolsCapable?: boolean;
  keyRef?: string;
  isActive?: boolean;
  isDefault?: boolean;
}

/**
 * `id` and `vendor` are absent on purpose — they are the row's billing identity.
 *
 * `keyRef` is omitted from the Partial and re-declared rather than intersected: an
 * intersection narrows to the OVERLAP of both members, which would drop the `null` that is
 * the only way to clear an override.
 */
export type UpdateLlmProviderDto = Partial<
  Omit<CreateLlmProviderDto, 'id' | 'vendor' | 'keyRef'>
> & {
  /** null clears the override, returning the row to the vendor's default key_ref. */
  keyRef?: string | null;
};

// --- Context ---
export interface Context {
  id: string;
  system: string;
  botId: string;
  name: string;
  context: string;
  description?: string;
}

export interface CreateContextDto {
  system: string;
  botId: string;
  name: string;
  context: string;
  description?: string;
}

export interface UpdateContextDto {
  context?: string;
  name?: string;
  description?: string;
}

// --- Persona ---
export interface Persona {
  id: string;
  system: string;
  botId: string;
  persona: string;
  name?: string;
  description?: string;
}

export interface CreatePersonaDto {
  system: string;
  botId: string;
  persona: string;
  name?: string;
  description?: string;
}

export interface UpdatePersonaDto {
  persona?: string;
  name?: string;
  description?: string;
}

// --- LLM registry: the priced catalog + the per-bot pointer into it ---
//
// Replaces two earlier shapes that are now gone: `llm_models` (per-bot rows that stored an
// apiKey — hub must never hold a key) and `bot_cloud_configs` (a per-bot cloud on/off flag
// plus its own llmModelId, which never had a row in production). Which model a bot uses is
// one nullable pointer on bot_runtime_config; null means "deployment default".
export interface LlmCatalogModel {
  id: string;
  vendor: string;
  model: string;
  label: string;
  inputPricePer1kMnt: number;
  outputPricePer1kMnt: number;
  toolsCapable: boolean;
  isDefault: boolean;
  /**
   * Whether this model can currently serve. `unknown` means tool-service could not be asked
   * — treat it as selectable, because greying out the whole picker during an unrelated
   * outage is worse than letting a pick fall back to the deployment default, which is what
   * an unusable model does anyway.
   */
  available: 'available' | 'unavailable' | 'unknown';
}

/** The bot's pointer already joined onto the catalog row. Carries no price, by design. */
export interface ResolvedLlm {
  modelId: string;
  label: string;
  vendor: string;
  model: string;
  params: Record<string, unknown> | null;
  toolsEnabled: boolean;
  stream: boolean;
  toolsCapable: boolean;
}

export interface BotRuntimeConfigResponse {
  system: string;
  botId: string;
  flowVersion: string;
  isActive: boolean;
  embeddingProfile: string;
  /** Plan C1: "Use intents" — hub's runtime answers this bot's typed messages from its intents. */
  intentsEnabled?: boolean;
  /** Per-bot no-LLM intent gate; null = deployment default (0.82 / 0.08). */
  intentMinScore?: number | null;
  intentMinMargin?: number | null;
  /** null => deployment default (tool-service resolves through EnvProfileResolver). */
  llm: ResolvedLlm | null;
}

export interface UpdateBotRuntimeConfigDto {
  flowVersion?: string;
  isActive?: boolean;
  /** null CLEARS the pointer back to the deployment default; omit to leave it alone. */
  llmModelId?: string | null;
  llmParams?: Record<string, unknown> | null;
  toolsEnabled?: boolean;
  llmStream?: boolean;
  intentsEnabled?: boolean;
  /** null resets to the deployment default; omit to leave it alone. */
  intentMinScore?: number | null;
  intentMinMargin?: number | null;
}

export interface IntentQuality {
  days: number;
  totals: { deflected: number; abstained: number; control: number; other: number; total: number };
  daily: { day: string; deflected: number; abstained: number; control: number; other: number }[];
  controlByCode: Record<string, number>;
  topUnmatched: { text: string; surface: 'comment' | 'dm' | null; seenCount: number; topScore: number | null; candidateCodes: string[] | null }[];
  reviewBacklog: number;
}

export interface GateView {
  effective: { minScore: number; minMargin: number; source: 'bot' | 'legacy' | 'default' };
  own: { minScore: number; minMargin: number; updatedBy: string | null; updatedAt: string } | null;
  defaults: { minScore: number; minMargin: number };
  bounds: { score: [number, number]; margin: [number, number] };
  history: { minScore: number | null; minMargin: number | null; prevMinScore: number | null; prevMinMargin: number | null; changedBy: string | null; createdAt: string }[];
}
export interface ShadowSurface {
  surface: 'dm' | 'comment';
  n: number;
  counts: Record<ShadowClass, number>;
  avgLatencyMs: number | null;
  hubOnPost?: { yes: number; no: number };
}
export interface ShadowReadinessData {
  days: number;
  state: string | null;
  surfaces: ShadowSurface[];
}
export interface ShadowItem {
  id: string;
  surface: 'dm' | 'comment';
  class: ShadowClass;
  text: string;
  rasaIntentId: string | null;
  hubIntentId: string | null;
  hubCode: string | null;
  hubScore: number | null;
  hubOnPost: boolean | null;
  verdict: 'rasa' | 'hub' | 'neither' | null;
  verdictBy: string | null;
  createdAt: string;
}
export interface GatePreview {
  days: number;
  n: number;
  smallSample: boolean;
  current: { minScore: number; minMargin: number; answered: number; handedOn: number };
  candidate: { minScore: number; minMargin: number; answered: number; handedOn: number };
  gained: number;
  lost: number;
  byIntent: { code: string; current: number; candidate: number }[];
  histogram: { score: { from: number; to: number; n: number }[]; margin: { from: number; to: number; n: number }[] };
}
export interface NearMiss {
  text: string;
  surface: 'comment' | 'dm' | null;
  topScore: number;
  candidateCodes: string[] | null;
  seenCount: number;
  lastSeenAt: string;
  /** True when the candidate score would answer it (it sits between the candidate and today's score). */
  wouldAnswer?: boolean;
}

// --- Comment moderation (hub /api/moderation; spec 2026-10-06-comment-moderation) ---

export interface ModerationHistoryRow {
  before: PolicyFields | null;
  after: PolicyFields | null;
  changedBy: string | null;
  createdAt: string;
}

export interface ModerationPolicyView {
  effective: PolicyFields & { source: 'bot' | 'platform' | 'default' };
  own: (PolicyFields & { updatedBy: string | null; updatedAt: string }) | null;
  platform: PolicyFields;
  history: ModerationHistoryRow[];
  run: EvalSummary | null;
}

export interface ModerationItem {
  id: string;
  system: string;
  botId: string;
  text: string;
  label: string | null;
  score: number | null;
  action: string | null;
  outcome: string | null;
  verdict: string | null;
  createdAt: string;
}

export interface ModerationMetrics {
  days: number;
  rows: { day: string; label: string; outcome: string; n: number }[];
  totals: { checked: number; hidden: number; undone: number; undoRate: number | null };
}

export interface ModerationEvalRun extends EvalSummary {
  split: string;
  confusion: Record<string, number>;
  ranBy: string | null;
}

export interface ModerationQueueStats {
  total: number;
  labelled: number;
  byVerdict: Record<string, number>;
}

export interface ModerationExampleRow {
  id: string;
  category: string;
  text: string;
  active: boolean;
  createdAt: string;
}

export interface ModerationCheck {
  id: string;
  channel: string;
  label: string;
  score: number;
  source: string;
  ruleId: string | null;
  action: string;
  mode: string;
  outcome: string;
  latencyMs: number;
  createdAt: string;
}

export interface ModerationHealth {
  system: string;
  checks1h: number;
  checks24h: number;
  lastAt: string | null;
  failed24h: number;
  stuckPending: number;
  flagged24h: number;
  avgLatencyMs1h: number | null;
}

export interface ModerationLexiconRow {
  id: string;
  term: string;
  active: boolean;
  addedBy: string | null;
  createdAt: string;
}

// --- Prompt Template ---
export interface PromptTemplate {
  id: string;
  system: string;
  botId: string;
  name: string;
  category?: string | null;
  template: string;
  variables?: Record<string, unknown> | null;
}

export interface CreatePromptTemplateDto {
  system: string;
  botId: string;
  name: string;
  category?: string;
  template: string;
  variables?: Record<string, unknown> | null;
}

export interface UpdatePromptTemplateDto {
  category?: string;
  template?: string;
  variables?: Record<string, unknown> | null;
  version?: number;
}

// --- Tool ---
export interface Tool {
  id: string;
  system: string;
  botId: string;
  name: string;
  type?: string;
  config: string;
  endpoint?: string;
  description?: string;
  priority?: number;
}

export interface CreateToolDto {
  system: string;
  botId: string;
  name: string;
  type?: string;
  config: string;
  endpoint?: string;
  description?: string;
  priority?: number;
}

export interface UpdateToolDto {
  name?: string;
  type?: string;
  config?: string;
  endpoint?: string;
  description?: string;
  priority?: number;
}

export interface TestToolDto {
  payload?: Record<string, unknown>;
  timeoutMs?: number;
  dryRun?: boolean;
}

export interface TestToolResult {
  success: boolean;
  text: string;
  data?: unknown;
}

// --- FAQ ---
export interface Faq {
  id: number;
  system: string;
  botId: string;
  question: string;
  answer: string;
  language?: string;
  category?: string;
}

export interface CreateFaqDto {
  system: string;
  botId: string;
  question: string;
  answer: string;
  language?: string;
  category?: string;
}

export interface UpdateFaqDto {
  question?: string;
  answer?: string;
  language?: string;
  category?: string;
}

// --- RAG Document ---
export interface RagDocument {
  id: number;
  system: string;
  botId: string;
  title: string;
  documentType: string;
  content?: string;
  language?: string;
  category?: string | null;
  description?: string | null;
  tags?: string[] | null;
  metadata?: Record<string, unknown> | null;
  source?: string | null;
  author?: string | null;
  status?: string;
  syncedToVector?: boolean;
  chunkCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

/** `GET /api/rag/documents` with `limit`/`offset`: one page plus the total matching the filters. */
export interface RagDocumentPage {
  items: RagDocument[];
  total: number;
  limit: number;
  offset: number;
}

export interface RagDocumentChunksInfo {
  documentId: number;
  documentTitle: string;
  /** Total number of chunks for the document (all pages). */
  chunkCount: number;
  /** Page size used for this response (Hub paged API). */
  limit?: number;
  /** Skip offset used for this response. */
  offset?: number;
  contentLength: number;
  syncedToVector: boolean;
  chunks: Array<{
    id: number;
    chunkIndex: number;
    content: string;
    startChar: number | null;
    endChar: number | null;
    chunkType: string | null;
    tokenCount: number | null;
    syncedToVector: boolean;
    createdAt: string;
  }>;
}

// --- Message bundles (Hub) ---
export interface MessageBundle {
  id: string;
  system: string;
  botId: string;
  tenantId?: string | null;
  channel: string;
  key: string;
  name: string;
  purpose?: string | null;
  payload_json: unknown[];
  required_slots: string[];
  intent_tags: string[];
  trigger_examples: string[];
  createdAt: string;
  updatedAt: string;
}

export interface MessageBundleListResponse {
  items: MessageBundle[];
  total: number;
}

export interface MessageBundleListQuery {
  system?: string;
  botId?: string;
  tenantId?: string;
  channel?: string;
  key?: string;
  limit?: number;
  offset?: number;
}

export interface CreateMessageBundleDto {
  system: string;
  botId: string;
  key: string;
  name: string;
  purpose?: string;
  tenantId?: string;
  channel?: string;
  payload_json: unknown[];
  required_slots?: string[];
  intent_tags?: string[];
  trigger_examples?: string[];
}

export interface UpdateMessageBundleDto {
  key?: string;
  name?: string;
  purpose?: string;
  payload_json?: unknown[];
  required_slots?: string[];
  intent_tags?: string[];
  trigger_examples?: string[];
}

export interface CreateDocumentDto {
  system: string;
  botId: string;
  title: string;
  documentType: string;
  content?: string;
  description?: string;
  language?: string;
  category?: string;
}

export interface UpdateDocumentDto {
  title?: string;
  documentType?: string;
  content?: string;
  description?: string;
  language?: string;
  category?: string;
}

// --- RAG Category ---
export interface RagCategory {
  id: number;
  key: string;
  label: string;
  description?: string;
  documentTypes?: DocumentType[] | null;
  isActive?: boolean;
  sortOrder?: number;
}

export type DocumentType = 'manual' | 'tutorial' | 'rule' | 'product' | 'faq' | 'other';

export interface CreateRagCategoryDto {
  key: string;
  label?: string;
  description?: string;
  documentTypes?: DocumentType[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateRagCategoryDto {
  key?: string;
  label?: string;
  description?: string;
  documentTypes?: DocumentType[];
  isActive?: boolean;
  sortOrder?: number;
}

// --- Reasoning Strategy ---
export interface ReasoningStrategy {
  id: string;
  system: string;
  botId: string;
  strategyType: string;
  config: Record<string, unknown>;
  enabled?: boolean;
  priority?: number;
  name?: string;
  description?: string;
}

export interface CreateReasoningStrategyDto {
  system: string;
  botId: string;
  strategyType: string;
  config: Record<string, unknown>;
  enabled?: boolean;
  priority?: number;
  name?: string;
  description?: string;
}

export interface UpdateReasoningStrategyDto {
  strategyType?: string;
  config?: Record<string, unknown>;
  enabled?: boolean;
  priority?: number;
  name?: string;
  description?: string;
}

export const hubService = {
  // Domain Profiles
  async listDomainProfiles(activeOnly?: boolean, token?: string | null): Promise<DomainProfile[]> {
    const qs = activeOnly ? '?activeOnly=true' : '';
    const res = await hubFetch(`/api/domain-profiles${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getDomainProfile(id: string, token?: string | null): Promise<DomainProfile> {
    const res = await hubFetch(`/api/domain-profiles/${id}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createDomainProfile(dto: CreateDomainProfileDto, token?: string | null): Promise<DomainProfile> {
    const res = await hubFetch('/api/domain-profiles', {
      method: 'POST',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateDomainProfile(id: string, dto: UpdateDomainProfileDto, token?: string | null): Promise<DomainProfile> {
    const res = await hubFetch(`/api/domain-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteDomainProfile(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/domain-profiles/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Generic Intents
  async listGenericIntents(includeInactive?: boolean, token?: string | null): Promise<GenericIntent[]> {
    const path = includeInactive ? '/api/generic-intents/all' : '/api/generic-intents';
    const res = await hubFetch(path, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createGenericIntent(dto: CreateGenericIntentDto, token?: string | null): Promise<GenericIntent> {
    const res = await hubFetch('/api/generic-intents', {
      method: 'POST',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateGenericIntent(code: string, dto: UpdateGenericIntentDto, token?: string | null): Promise<GenericIntent> {
    const res = await hubFetch(`/api/generic-intents/${encodeURIComponent(code)}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteGenericIntent(code: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/generic-intents/${encodeURIComponent(code)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async syncGenericIntents(token?: string | null): Promise<GenericIntentSyncResult> {
    const res = await hubFetch('/api/generic-intents/sync', { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // Per-bot generic-intent extension rows (config cascade tier 2)
  // A1: keyed by (system, botId, code) — the bot's system rides on every call.
  async listBotGenericIntentConfigs(bot: BotRef, token?: string | null): Promise<BotGenericIntentConfig[]> {
    const res = await hubFetch(botConfigListPath(bot), { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async upsertBotGenericIntentConfig(dto: UpsertBotGenericIntentConfigDto, token?: string | null): Promise<BotGenericIntentConfig> {
    const res = await hubFetch('/api/generic-intents/bot-config', {
      method: 'PUT',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteBotGenericIntentConfig(bot: BotRef, code: string, token?: string | null): Promise<void> {
    const res = await hubFetch(
      botConfigDeletePath(bot, code),
      { method: 'DELETE' },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
  },

  // Intents (per bot)
  async listIntentsByBot(botId: string, system?: string, token?: string | null): Promise<Intent[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listIntentSummaries(botId: string, system: string, token?: string | null): Promise<IntentSummary[]> {
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/summary?system=${encodeURIComponent(system)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listConflicts(botId: string, system: string, token?: string | null): Promise<IntentConflict[]> {
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/conflicts?system=${encodeURIComponent(system)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listV2Blocks(botId: string, channel: string, q: string, token?: string | null): Promise<{ available: boolean; blocks: V2Block[] }> {
    const qs = new URLSearchParams({ system: 'chatbot_v2', channel, ...(q.trim() ? { q: q.trim() } : {}) });
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/v2-blocks?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getIntentQuality(botId: string, system: string, days: number, token?: string | null): Promise<IntentQuality> {
    const res = await hubFetch(
      `/api/intents/bot/${encodeURIComponent(botId)}/quality?system=${encodeURIComponent(system)}&days=${days}`,
      { method: 'GET' },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getIntentGate(system: string, botId: string, token?: string | null): Promise<GateView> {
    const res = await hubFetch(`/api/intent-gate/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async setIntentGate(system: string, botId: string, pair: { minScore: number; minMargin: number }, token?: string | null): Promise<GateView> {
    const res = await hubFetch(`/api/intent-gate/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'PUT', body: JSON.stringify(pair) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async resetIntentGate(system: string, botId: string, token?: string | null): Promise<GateView> {
    const res = await hubFetch(`/api/intent-gate/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getGatePreview(botId: string, system: string, days: number, minScore: number, minMargin: number, token?: string | null): Promise<GatePreview> {
    const qs = new URLSearchParams({ system, days: String(days), minScore: String(minScore), minMargin: String(minMargin) });
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/gate-preview?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getNearMisses(botId: string, system: string, minScore: number, token?: string | null): Promise<NearMiss[]> {
    const qs = new URLSearchParams({ system, minScore: String(minScore), limit: '20' });
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/near-misses?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // --- Hub vs Rasa shadow comparison ---
  async getShadowReadiness(botId: string, system: string, days: number, token?: string | null): Promise<ShadowReadinessData> {
    const qs = new URLSearchParams({ system, days: String(days) });
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/shadow?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listShadowItems(
    botId: string,
    system: string,
    opts: { surface?: 'dm' | 'comment'; pending?: boolean; limit?: number; offset?: number },
    token?: string | null,
  ): Promise<{ rows: ShadowItem[]; total: number }> {
    const qs = new URLSearchParams({ system, limit: String(opts.limit ?? 50), offset: String(opts.offset ?? 0) });
    if (opts.surface) qs.set('surface', opts.surface);
    if (opts.pending) qs.set('pending', 'true');
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/shadow/items?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async shadowVerdict(botId: string, system: string, id: string, verdict: 'rasa' | 'hub' | 'neither', token?: string | null): Promise<void> {
    const res = await hubFetch(
      `/api/intents/bot/${encodeURIComponent(botId)}/shadow/items/${encodeURIComponent(id)}/verdict`,
      { method: 'POST', body: JSON.stringify({ system, verdict }) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
  },

  // --- Comment moderation ---

  async getModerationPolicy(system: string, botId: string, token?: string | null): Promise<ModerationPolicyView> {
    const res = await hubFetch(`/api/moderation/policy/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async setModerationPolicy(system: string, botId: string, fields: PolicyFields, token?: string | null): Promise<ModerationPolicyView> {
    const res = await hubFetch(`/api/moderation/policy/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'PUT', body: JSON.stringify(fields) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async resetModerationPolicy(system: string, botId: string, token?: string | null): Promise<ModerationPolicyView> {
    const res = await hubFetch(`/api/moderation/policy/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async setPlatformModerationPolicy(fields: PolicyFields, token?: string | null): Promise<ModerationPolicyView> {
    const res = await hubFetch(`/api/moderation/platform-policy`, { method: 'PUT', body: JSON.stringify(fields) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listModerationItems(
    system: string,
    botId: string,
    q: { limit: number; offset: number; label?: string; outcome?: string },
    token?: string | null,
  ): Promise<{ rows: ModerationItem[]; total: number }> {
    const qs = new URLSearchParams({ system, botId, limit: String(q.limit), offset: String(q.offset) });
    if (q.label) qs.set('label', q.label);
    if (q.outcome) qs.set('outcome', q.outcome);
    const res = await hubFetch(`/api/moderation/items?${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async moderationVerdict(system: string, botId: string, id: string, verdict: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/items/${encodeURIComponent(id)}/verdict`, { method: 'POST', body: JSON.stringify({ system, botId, verdict }) }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async moderationUnhide(system: string, botId: string, id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/items/${encodeURIComponent(id)}/unhide`, { method: 'POST', body: JSON.stringify({ system, botId }) }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async getModerationMetrics(system: string, botId: string, days: number, token?: string | null): Promise<ModerationMetrics> {
    const res = await hubFetch(`/api/moderation/metrics/${encodeURIComponent(system)}/${encodeURIComponent(botId)}?days=${days}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getModerationRecent(system: string, botId: string, limit: number, token?: string | null): Promise<ModerationCheck[]> {
    const res = await hubFetch(`/api/moderation/recent/${encodeURIComponent(system)}/${encodeURIComponent(botId)}?limit=${limit}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getModerationHealth(token?: string | null): Promise<ModerationHealth[]> {
    const res = await hubFetch(`/api/moderation/admin/health`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async moderationQueue(q: { limit: number }, token?: string | null): Promise<{ rows: ModerationItem[]; total: number }> {
    const res = await hubFetch(`/api/moderation/admin/queue?pending=1&limit=${q.limit}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async moderationQueueStats(token?: string | null): Promise<ModerationQueueStats> {
    const res = await hubFetch(`/api/moderation/admin/queue/stats`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async moderationQueueVerdict(id: string, verdict: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/admin/queue/${encodeURIComponent(id)}/verdict`, { method: 'POST', body: JSON.stringify({ verdict }) }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async runModerationEval(token?: string | null): Promise<ModerationEvalRun> {
    const res = await hubFetch(`/api/moderation/admin/eval`, { method: 'POST', body: JSON.stringify({ limit: 5000 }) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Empty body when no evaluation has run yet. */
  async latestModerationEval(token?: string | null): Promise<ModerationEvalRun | null> {
    const res = await hubFetch(`/api/moderation/admin/eval/latest`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    const t = await res.text();
    return t ? JSON.parse(t) : null;
  },

  async moderationExamples(token?: string | null): Promise<ModerationExampleRow[]> {
    const res = await hubFetch(`/api/moderation/admin/examples`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async addModerationExamples(items: { category: string; text: string }[], token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/admin/examples`, { method: 'POST', body: JSON.stringify({ items }) }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async deactivateModerationExample(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/admin/examples/${encodeURIComponent(id)}/deactivate`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async moderationLexicon(token?: string | null): Promise<ModerationLexiconRow[]> {
    const res = await hubFetch(`/api/moderation/admin/lexicon`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async addModerationLexicon(terms: string[], token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/admin/lexicon`, { method: 'POST', body: JSON.stringify({ terms }) }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async deactivateModerationLexicon(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/moderation/admin/lexicon/${encodeURIComponent(id)}/deactivate`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async staleBlocks(botId: string, token?: string | null): Promise<{ available: boolean; stale: Record<string, string[]> }> {
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/stale-blocks?system=chatbot_v2`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getImportStatus(botId: string, system: string, token?: string | null): Promise<ImportStatus | null> {
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/import-status?system=${encodeURIComponent(system)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    const text = await res.text();
    return text ? (JSON.parse(text) as ImportStatus | null) : null;
  },

  async getIntentByBotAndCode(botId: string, intentCode: string, system?: string, token?: string | null): Promise<Intent> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/intent/${encodeURIComponent(intentCode)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createIntent(dto: CreateIntentDto, token?: string | null): Promise<Intent> {
    const res = await hubFetch('/api/intents', {
      method: 'POST',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateIntent(botId: string, intentCode: string, dto: UpdateIntentDto, system?: string, token?: string | null): Promise<Intent> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/intent/${encodeURIComponent(intentCode)}${qs}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteIntent(botId: string, intentCode: string, system?: string, token?: string | null): Promise<void> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/intents/bot/${encodeURIComponent(botId)}/intent/${encodeURIComponent(intentCode)}${qs}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Intent texts (use intent entity id from listIntentsByBot)
  async listIntentTexts(intentId: string, token?: string | null, source?: 'rasa' | 'operator' | 'review'): Promise<IntentText[]> {
    const qs = source ? `?source=${source}` : '';
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createIntentText(intentId: string, text: string, token?: string | null, source?: 'operator' | 'review'): Promise<IntentText> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts`, {
      method: 'POST',
      body: JSON.stringify({ text, ...(source ? { source } : {}) }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createBulkIntentTexts(intentId: string, texts: string[], token?: string | null, source?: 'operator' | 'review'): Promise<IntentText[]> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts`, {
      method: 'POST',
      body: JSON.stringify({ texts, ...(source ? { source } : {}) }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteIntentText(intentId: string, textId: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts/${encodeURIComponent(textId)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async generateIntentTexts(intentId: string, texts: string[], token?: string | null): Promise<IntentText[]> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts/generate`, {
      method: 'POST',
      body: JSON.stringify({ texts }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getIntentTextsSyncStatus(intentId: string, token?: string | null): Promise<IntentSyncStatus> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/texts/sync-status`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async syncIntentToQdrant(intentId: string, token?: string | null): Promise<{ synced: number; failed: number }> {
    const res = await hubFetch(`/api/intents/${encodeURIComponent(intentId)}/sync`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // Bot (enabledDomains for coarse router)
  async getBot(botId: string, system?: string, token?: string | null): Promise<Bot> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/bots/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Returns null if bot does not exist in Hub (404). Use for create-on-demand UX. */
  async getBotOrNull(botId: string, system?: string, token?: string | null): Promise<Bot | null> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/bots/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Create/register bot in Hub. Idempotent - updates if exists. Use when bot exists in legacy but not in Hub. */
  async createBot(
    botId: string,
    system: BotSystem,
    name?: string,
    token?: string | null,
  ): Promise<Bot> {
    const nameStr = String(name ?? botId ?? 'Bot')
    const res = await hubFetch('/api/bots', {
      method: 'POST',
      body: JSON.stringify({
        system,
        botId: `${botId}`,
        name: nameStr,
      }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateBotEnabledDomains(botId: string, enabledDomains: string[] | null, system: BotSystem, token?: string | null): Promise<Bot> {
    const res = await hubFetch(botPatchPath({ system, botId }), {
      method: 'PATCH',
      body: JSON.stringify({ enabledDomains, system }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // LLM Cache
  async getCacheStatus(botId?: string, system?: string, token?: string | null): Promise<LlmCacheStatus> {
    const path = botId
      ? `/api/llm/cache/bots/${encodeURIComponent(botId)}/status${system ? `?system=${encodeURIComponent(system)}` : ''}`
      : '/api/llm/cache/status';
    const res = await hubFetch(path, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getCacheStats(botId: string, system?: string, token?: string | null): Promise<LlmCacheStats> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/stats${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listResponseCache(
    botId: string,
    system?: string,
    limit?: number,
    token?: string | null,
  ): Promise<RedisCacheEntry[]> {
    const params = new URLSearchParams();
    if (system) params.set('system', system);
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString() ? `?${params}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/response${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listRetrievalCache(
    botId: string,
    system?: string,
    limit?: number,
    token?: string | null,
  ): Promise<RedisCacheEntry[]> {
    const params = new URLSearchParams();
    if (system) params.set('system', system);
    if (limit != null) params.set('limit', String(limit));
    const qs = params.toString() ? `?${params}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/retrieval${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateCacheEnabled(botId: string, enabled: boolean, system?: string, token?: string | null): Promise<Bot> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/config${qs}`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateCacheConfig(
    botId: string,
    config: { semantic?: boolean | null; response?: boolean | null; retrieval?: boolean | null },
    system?: string,
    token?: string | null,
  ): Promise<Bot> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/config${qs}`, {
      method: 'PATCH',
      body: JSON.stringify(config),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listSemanticCache(
    botId: string,
    system?: string,
    limit?: number,
    offset?: string,
    token?: string | null,
  ): Promise<{ items: SemanticCacheEntry[]; nextOffset?: unknown }> {
    const params = new URLSearchParams();
    if (system) params.set('system', system);
    if (limit != null) params.set('limit', String(limit));
    if (offset) params.set('offset', offset);
    const qs = params.toString() ? `?${params}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/semantic${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createSemanticCache(
    botId: string,
    query: string,
    response: string,
    system?: string,
    token?: string | null,
  ): Promise<SemanticCacheEntry> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/semantic${qs}`, {
      method: 'POST',
      body: JSON.stringify({ query, response }),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateSemanticCache(
    botId: string,
    id: string,
    updates: { query?: string; response?: string },
    system?: string,
    token?: string | null,
  ): Promise<SemanticCacheEntry> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/semantic/${encodeURIComponent(id)}${qs}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteSemanticCache(
    botId: string,
    id: string,
    system?: string,
    token?: string | null,
  ): Promise<void> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/semantic/${encodeURIComponent(id)}${qs}`, {
      method: 'DELETE',
    }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async promoteSemanticToFaq(
    botId: string,
    id: string,
    system?: string,
    body?: { category?: string; language?: string },
    token?: string | null,
  ): Promise<Faq> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/semantic/${encodeURIComponent(id)}/promote-to-faq${qs}`, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async clearBotCache(botId: string, system?: string, token?: string | null): Promise<LlmCacheStats> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/llm/cache/bots/${encodeURIComponent(botId)}/clear${qs}`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // LLM Token Usage
  async listLlmUsage(
    params: { system?: string; botId?: string; from?: string; to?: string; source?: string; provider?: string; callType?: string; limit?: number; offset?: number },
    token?: string | null,
  ): Promise<{ items: LlmTokenUsageEntry[]; total: number }> {
    const search = new URLSearchParams();
    if (params.system) search.set('system', params.system);
    if (params.botId) search.set('botId', params.botId);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    if (params.source) search.set('source', params.source);
    if (params.provider) search.set('provider', params.provider);
    if (params.callType) search.set('callType', params.callType);
    if (params.limit != null) search.set('limit', String(params.limit));
    if (params.offset != null) search.set('offset', String(params.offset));
    const qs = search.toString() ? `?${search}` : '';
    const res = await hubFetch(`/api/llm/usage${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getLlmUsageSummary(
    params: { system?: string; botId?: string; from?: string; to?: string },
    token?: string | null,
  ): Promise<LlmUsageSummaryRow[]> {
    const search = new URLSearchParams();
    if (params.system) search.set('system', params.system);
    if (params.botId) search.set('botId', params.botId);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    const qs = search.toString() ? `?${search}` : '';
    const res = await hubFetch(`/api/llm/usage/summary${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // --- Bot wallet (LLM usage phase 1a) ---
  async getWallet(system: string, botId: string, token?: string | null): Promise<BotWalletView> {
    const res = await hubFetch(`/api/wallet/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** A global admin's top-up or correction; refusals come back as data so the form can say why. */
  async recordWalletMovement(
    kind: 'topups' | 'adjustments',
    system: string,
    botId: string,
    body: { amountMnt: number; ref: string; note?: string },
    token?: string | null,
  ): Promise<WalletMovementResult> {
    const res = await hubFetch(
      `/api/wallet/${encodeURIComponent(system)}/${encodeURIComponent(botId)}/${kind}`,
      { method: 'POST', body: JSON.stringify(body) },
      token,
    );
    if (!res.ok) return { ok: false, status: res.status, message: await res.text() };
    const data = await res.json();
    return { ok: true, balanceMnt: data.balanceMnt, duplicate: !!data.duplicate };
  },

  // --- Embedding profiles (Platform › Embedding profiles, global admins) ---
  async listEmbeddingProfilesAdmin(): Promise<EmbeddingProfileRow[]> {
    const res = await hubFetch('/api/embedding-profile/admin', { method: 'GET' });
    if (!res.ok) throw new HubWriteError(res.status, await res.text());
    return res.json();
  },

  /** Writes answer `{ok:false, status, message}` instead of throwing, so the form can say why. */
  async writeEmbeddingProfile<T>(
    method: 'POST' | 'PUT' | 'DELETE',
    path: '' | `/${string}`,
    body?: Record<string, unknown>,
  ): Promise<{ ok: true; data: T } | { ok: false; status: number; message: string }> {
    let res: Response;
    try {
      res = await hubFetch(`/api/embedding-profile/admin${path}`, {
        method,
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
    } catch (e) {
      return { ok: false, status: 0, message: e instanceof Error ? e.message : String(e) };
    }
    if (!res.ok) return { ok: false, status: res.status, message: await res.text() };
    return { ok: true, data: (await res.json()) as T };
  },

  // --- LLM Pool Admin ---
  async listLlmPools(token?: string | null): Promise<HubLlmPool[]> {
    const res = await hubFetch('/api/llm/pool/pools', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getLlmPool(id: string, token?: string | null): Promise<HubLlmPool> {
    const res = await hubFetch(`/api/llm/pool/pools/${encodeURIComponent(id)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createLlmPool(
    dto: {
      key: string;
      name: string;
      description?: string | null;
      provider: string;
      apiKey?: string | null;
      endpoints: Record<string, string>;
      providerConfig?: Record<string, unknown>;
      enabled?: boolean;
      draining?: boolean;
      models?: Array<{
        modelKey: string;
        displayName?: string | null;
        endpointKey?: string | null;
        enabled?: boolean;
      }>;
    },
    token?: string | null,
  ): Promise<HubLlmPool> {
    const res = await hubFetch('/api/llm/pool/pools', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async patchLlmPool(
    id: string,
    dto: {
      key?: string;
      name?: string;
      description?: string | null;
      provider?: string;
      apiKey?: string | null;
      endpoints?: Record<string, string>;
      providerConfig?: Record<string, unknown>;
      enabled?: boolean;
      draining?: boolean;
    },
    token?: string | null,
  ): Promise<HubLlmPool> {
    const res = await hubFetch(`/api/llm/pool/pools/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteLlmPool(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/llm/pool/pools/${encodeURIComponent(id)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async addLlmPoolModel(
    poolId: string,
    dto: {
      modelKey: string;
      displayName?: string | null;
      endpointKey?: string | null;
      enabled?: boolean;
    },
    token?: string | null,
  ): Promise<HubLlmPoolModel> {
    const res = await hubFetch(
      `/api/llm/pool/pools/${encodeURIComponent(poolId)}/models`,
      { method: 'POST', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async patchLlmPoolModel(
    id: string,
    dto: {
      modelKey?: string;
      displayName?: string | null;
      endpointKey?: string | null;
      enabled?: boolean;
    },
    token?: string | null,
  ): Promise<HubLlmPoolModel> {
    const res = await hubFetch(`/api/llm/pool/models/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteLlmPoolModel(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/llm/pool/models/${encodeURIComponent(id)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async getLlmPoolUsageRealtime(token?: string | null): Promise<LlmPoolUsageCounterRow[]> {
    const res = await hubFetch('/api/llm/pool/usage/realtime', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getLlmPoolUsageSummary(
    params: { system?: string; botId?: string; from?: string; to?: string; provider?: string },
    token?: string | null,
  ): Promise<LlmPoolUsageCounterRow[]> {
    const search = new URLSearchParams();
    if (params.system) search.set('system', params.system);
    if (params.botId) search.set('botId', params.botId);
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    if (params.provider) search.set('provider', params.provider);
    const qs = search.toString() ? `?${search}` : '';

    const res = await hubFetch(`/api/llm/pool/usage/summary${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listLlmPoolPolicies(token?: string | null): Promise<LlmPoolPolicy[]> {
    const res = await hubFetch('/api/llm/pool/policies', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createLlmPoolPolicy(
    dto: {
      name: string;
      system?: string | null;
      botId?: string | null;
      strategy?: string;
      fallbackProviders?: string;
      maxRetries?: number;
      enabled?: boolean;
    },
    token?: string | null,
  ): Promise<LlmPoolPolicy> {
    const res = await hubFetch('/api/llm/pool/policies', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async patchLlmPoolPolicy(
    id: string,
    dto: { enabled?: boolean; strategy?: string; fallbackProviders?: string; maxRetries?: number },
    token?: string | null,
  ): Promise<LlmPoolPolicy> {
    const res = await hubFetch(`/api/llm/pool/policies/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** POST /api/agent/route — semantic router → prompt DAG / executors; session in Redis (REDIS_URL required on hub). */
  async agentRoute(dto: AgentRouteDto, token?: string | null): Promise<AgentRouteResult> {
    const res = await hubFetch('/api/agent/route', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** POST /api/agent-runtime/route — LLM-first runtime route+execute with trace. */
  async agentRuntimeRoute(
    dto: AgentRuntimeRouteDto,
    token?: string | null,
  ): Promise<AgentRuntimeRouteResult> {
    const res = await hubFetch('/api/agent-runtime/route', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listAgentPlannerProfiles(token?: string | null): Promise<AgentPlannerProfile[]> {
    const res = await hubFetch('/api/agent-planner/profiles', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createAgentPlannerProfile(
    dto: {
      name: string;
      system: string;
      botId: string;
      enabled?: boolean;
      systemPromptOverride?: string | null;
      maxLoopIterations?: number | null;
      toolRankTopK?: number | null;
      enableToolRanking?: boolean | null;
      preRetrieveRag?: boolean | null;
      minConfidence?: number | null;
      includeBuiltinTools?: boolean;
      appendIntentCapabilities?: boolean | null;
      appendToolCapabilities?: boolean | null;
      appendServiceCapabilities?: boolean | null;
      appendRagGuidance?: boolean | null;
    },
    token?: string | null,
  ): Promise<AgentPlannerProfile> {
    const res = await hubFetch('/api/agent-planner/profiles', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async patchAgentPlannerProfile(
    id: string,
    dto: {
      name?: string;
      enabled?: boolean;
      systemPromptOverride?: string | null;
      maxLoopIterations?: number | null;
      toolRankTopK?: number | null;
      enableToolRanking?: boolean | null;
      preRetrieveRag?: boolean | null;
      minConfidence?: number | null;
      includeBuiltinTools?: boolean;
      appendIntentCapabilities?: boolean | null;
      appendToolCapabilities?: boolean | null;
      appendServiceCapabilities?: boolean | null;
      appendRagGuidance?: boolean | null;
    },
    token?: string | null,
  ): Promise<AgentPlannerProfile> {
    const res = await hubFetch(
      `/api/agent-planner/profiles/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteAgentPlannerProfile(id: string, token?: string | null): Promise<{ ok: true }> {
    const res = await hubFetch(`/api/agent-planner/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // --- Agent workflows ---
  async listAgentWorkflows(
    query: { system?: string; botId?: string },
    token?: string | null,
  ): Promise<AgentWorkflow[]> {
    const params = new URLSearchParams();
    if (query.system) params.set('system', query.system);
    if (query.botId) params.set('botId', query.botId);
    const qs = params.toString();
    const res = await hubFetch(`/api/agent-workflows${qs ? `?${qs}` : ''}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getAgentWorkflow(id: string, token?: string | null): Promise<AgentWorkflow> {
    const res = await hubFetch(`/api/agent-workflows/${encodeURIComponent(id)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createAgentWorkflow(dto: CreateAgentWorkflowDto, token?: string | null): Promise<AgentWorkflow> {
    const res = await hubFetch('/api/agent-workflows', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateAgentWorkflow(
    id: string,
    dto: UpdateAgentWorkflowDto,
    token?: string | null,
  ): Promise<AgentWorkflow> {
    const res = await hubFetch(
      `/api/agent-workflows/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async publishAgentWorkflow(
    id: string,
    dto?: PublishAgentWorkflowDto,
    token?: string | null,
  ): Promise<AgentWorkflow> {
    const res = await hubFetch(
      `/api/agent-workflows/${encodeURIComponent(id)}/publish`,
      { method: 'POST', body: JSON.stringify(dto ?? {}) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async compileAgentWorkflow(
    id: string,
    token?: string | null,
  ): Promise<CompileAgentWorkflowResult> {
    const res = await hubFetch(`/api/agent-workflows/${encodeURIComponent(id)}/compile`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteAgentWorkflow(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/agent-workflows/${encodeURIComponent(id)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  async listAgentWorkflowNodeTypes(
    flowType?: string,
    token?: string | null,
  ): Promise<AgentWorkflowNodeTypeDef[]> {
    const qs = flowType ? `?flowType=${encodeURIComponent(flowType)}` : '';
    const res = await hubFetch(`/api/agent-workflows/node-types${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listAgentWorkflowEdgeTypes(
    flowType?: string,
    token?: string | null,
  ): Promise<AgentWorkflowEdgeTypeDef[]> {
    const qs = flowType ? `?flowType=${encodeURIComponent(flowType)}` : '';
    const res = await hubFetch(`/api/agent-workflows/edge-types${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** POST /api/agent-planner/process — planner + tools loop (see hub docs). */
  async agentPlannerProcess(
    dto: {
      message: string;
      userId: string;
      system?: string;
      botId?: string;
      maxLoopIterations?: number;
      enableToolRanking?: boolean;
      toolRankTopK?: number;
      includePlan?: boolean;
      preRetrieveRag?: boolean;
      minConfidence?: number;
    },
    token?: string | null,
  ): Promise<AgentPlannerProcessResult> {
    const res = await hubFetch('/api/agent-planner/process', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Smoke-test pool path: mode(text|structured|tool|stream) + adapter (provider-dependent). */
  async inferenceGenerate(
    dto: {
      systemPrompt: string;
      userPrompt: string;
      mode?: 'text' | 'structured' | 'tool' | 'stream';
      /** Adapter: http | langchain (OpenAI-compatible); genai-sdk | genai-http for Gemini pools. */
      adapter?: 'http' | 'langchain' | 'genai-http' | 'genai-sdk';
      /** Overrides pool `provider_config.transport` for this request only (pool test). */
      transport?: string;
      /** Pool node provider filter */
      provider?: string;
      /** Pool node model id filter (exact match to node.model) */
      model?: string;
      temperature?: number;
      maxTokens?: number;
      tools?: InferenceToolInput[];
      toolCallContext?: Record<string, string>;
    },
    token?: string | null,
  ): Promise<InferenceGenerateResult> {
    const res = await hubFetch('/api/inference/generate', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // LLM registry: the priced catalog, and the per-bot pointer into it.
  async listLlmCatalog(token?: string | null): Promise<LlmCatalogModel[]> {
    const res = await hubFetch('/api/llm-model', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // null on 404, deliberately. "This bot has never been configured" is the normal state for
  // most bots, not an error — everything falls back to the deployment default — so the tab
  // must be able to render it without showing a failure.
  async getBotRuntimeConfig(
    system: string,
    botId: string,
    token?: string | null,
  ): Promise<BotRuntimeConfigResponse | null> {
    const res = await hubFetch(
      `/api/bot-runtime-config/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`,
      { method: 'GET' },
      token,
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateBotRuntimeConfig(
    system: string,
    botId: string,
    dto: UpdateBotRuntimeConfigDto,
    token?: string | null,
  ): Promise<BotRuntimeConfigResponse> {
    const res = await hubFetch(
      `/api/bot-runtime-config/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`,
      { method: 'PUT', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // LLM provider catalog — the operator's side of the same table `listLlmCatalog` reads.
  // Separate endpoint, not a flag: this one carries USD costs and keyRef, which must never
  // reach a bot owner.
  async listLlmProviders(token?: string | null): Promise<OperatorLlmProvider[]> {
    const res = await hubFetch('/api/llm-provider', { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createLlmProvider(
    dto: CreateLlmProviderDto,
    token?: string | null,
  ): Promise<OperatorLlmProvider> {
    const res = await hubFetch('/api/llm-provider', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateLlmProvider(
    id: string,
    dto: UpdateLlmProviderDto,
    token?: string | null,
  ): Promise<OperatorLlmProvider> {
    const res = await hubFetch(
      `/api/llm-provider/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // Context
  async listContextsByBot(botId: string, system?: string, token?: string | null): Promise<Context[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/contexts/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createContext(dto: CreateContextDto, token?: string | null): Promise<Context> {
    const res = await hubFetch('/api/contexts', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateContext(id: string, dto: UpdateContextDto, token?: string | null): Promise<Context> {
    const res = await hubFetch(`/api/contexts/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteContext(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/contexts/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Persona
  async listPersonasByBot(botId: string, system?: string, token?: string | null): Promise<Persona[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/agent-personas/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createPersona(dto: CreatePersonaDto, token?: string | null): Promise<Persona> {
    const res = await hubFetch('/api/agent-personas', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updatePersona(id: string, dto: UpdatePersonaDto, token?: string | null): Promise<Persona> {
    const res = await hubFetch(`/api/agent-personas/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deletePersona(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/agent-personas/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Prompt Templates
  async listPromptTemplates(
    botId?: string,
    system?: string,
    token?: string | null,
    category?: string,
  ): Promise<PromptTemplate[]> {
    const params = new URLSearchParams();
    if (botId) params.set('botId', botId);
    if (system) params.set('system', system);
    if (category) params.set('category', category);
    const qs = params.toString() ? `?${params}` : '';
    const res = await hubFetch(`/api/prompt-templates${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async listPromptTemplatesByBot(
    botId: string,
    system?: string,
    token?: string | null,
    category?: string,
  ): Promise<PromptTemplate[]> {
    const params = new URLSearchParams();
    if (system) params.set('system', system);
    if (category) params.set('category', category);
    const qs = params.toString() ? `?${params}` : '';
    const res = await hubFetch(`/api/prompt-templates/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createPromptTemplate(dto: CreatePromptTemplateDto, token?: string | null): Promise<PromptTemplate> {
    const res = await hubFetch('/api/prompt-templates', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updatePromptTemplate(id: string, dto: UpdatePromptTemplateDto, token?: string | null): Promise<PromptTemplate> {
    const res = await hubFetch(`/api/prompt-templates/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deletePromptTemplate(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/prompt-templates/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Tools
  async listToolsByBot(botId: string, system?: string, token?: string | null): Promise<Tool[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/tools/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createTool(dto: CreateToolDto, token?: string | null): Promise<Tool> {
    const res = await hubFetch('/api/tools', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateTool(id: string, dto: UpdateToolDto, token?: string | null): Promise<Tool> {
    const res = await hubFetch(`/api/tools/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async testTool(id: string, dto: TestToolDto, token?: string | null): Promise<TestToolResult> {
    const res = await hubFetch(`/api/tools/${id}/test`, { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteTool(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/tools/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // FAQ
  async listFaqsByBot(botId: string, system?: string, token?: string | null): Promise<Faq[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/faqs/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createFaq(dto: CreateFaqDto, token?: string | null): Promise<Faq> {
    const res = await hubFetch('/api/faqs', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateFaq(id: string, dto: UpdateFaqDto, token?: string | null): Promise<Faq> {
    const res = await hubFetch(`/api/faqs/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteFaq(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/faqs/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // RAG Documents
  async listDocumentsByBot(botId: string, system?: string, token?: string | null): Promise<RagDocument[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/rag/documents/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createDocument(dto: CreateDocumentDto, token?: string | null): Promise<RagDocument> {
    const res = await hubFetch('/api/rag/documents', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateDocument(id: string, dto: UpdateDocumentDto, token?: string | null): Promise<RagDocument> {
    const res = await hubFetch(`/api/rag/documents/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteDocument(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/rag/documents/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  /** List RAG documents with optional filters (Hub `GET /api/rag/documents`). */
  async listRagDocuments(
    filters: {
      botId?: string;
      system?: string;
      documentType?: string;
      language?: string;
      category?: string;
    },
    token?: string | null,
  ): Promise<RagDocument[]> {
    const params = new URLSearchParams();
    if (filters.botId) params.set('botId', filters.botId);
    if (filters.system) params.set('system', filters.system);
    if (filters.documentType) params.set('documentType', filters.documentType);
    if (filters.language) params.set('language', filters.language);
    if (filters.category) params.set('category', filters.category);
    const qs = params.toString();
    const res = await hubFetch(`/api/rag/documents${qs ? `?${qs}` : ''}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Knowledge › Documents: one page (query from lib/documentForm `documentsListQuery`). */
  async listRagDocumentsPage(query: string): Promise<RagDocumentPage> {
    const res = await hubFetch(`/api/rag/documents?${query}`, { method: 'GET' });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** Create (`id` null) or update a document: JSON for text, multipart when a file replaces the content. */
  async saveRagDocument(id: number | null, fields: Record<string, string | string[]>, file: File | null): Promise<RagDocument> {
    const path = id == null ? '/api/rag/documents' : `/api/rag/documents/${id}`;
    const method = id == null ? 'POST' : 'PUT';
    const res = file
      ? await hubFetchForm(path, method, fields, file)
      : await hubFetch(path, { method, body: JSON.stringify(fields) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getRagDocumentById(id: number, token?: string | null): Promise<RagDocument> {
    const res = await hubFetch(`/api/rag/documents/${id}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getRagDocumentChunks(
    id: number,
    options?: { token?: string | null; limit?: number; offset?: number },
  ): Promise<RagDocumentChunksInfo> {
    const params = new URLSearchParams();
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    const res = await hubFetch(
      `/api/rag/documents/${id}/chunks${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      options?.token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async syncRagDocument(id: number, token?: string | null): Promise<{ message: string }> {
    const res = await hubFetch(`/api/rag/documents/${id}/sync`, { method: 'POST' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  // Knowledge › Builder + Prompts (A2 step B): hub fronts llm-tester at /api/knowledge/:system/:botId/* (bot-scoped;
  // the browser never calls llm-tester). Errors carry the hub's JSON body — show them with lib/assistantView errorText.
  async knowledge<T = any>(system: string, botId: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE', rest: string, body?: unknown): Promise<T> {
    const res = await hubFetch(knowledgePath(system, botId, rest), {
      method,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
    if (!res.ok) throw new Error(await res.text());
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  },

  /** A PDF / TXT / MD file's text for a build (the hub's document parser; nothing stored). */
  async knowledgeExtract(system: string, botId: string, file: File): Promise<{ name: string; content: string }> {
    const res = await hubFetchForm(knowledgePath(system, botId, 'extract'), 'POST', {}, file);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  /** A job's live progress (SSE through the hub). Read with a stream reader — EventSource cannot send the bearer. */
  async openKnowledgeJobEvents(system: string, botId: string, jobId: string, lastEventId: string | null, signal: AbortSignal): Promise<Response> {
    const q = lastEventId ? `?lastEventId=${encodeURIComponent(lastEventId)}` : '';
    return hubFetch(knowledgePath(system, botId, `jobs/${encodeURIComponent(jobId)}/events${q}`), {
      method: 'GET',
      headers: { Accept: 'text/event-stream' },
      signal,
    });
  },

  // RAG Categories (system-wide)
  async listRagCategories(activeOnly?: boolean, token?: string | null): Promise<RagCategory[]> {
    const qs = activeOnly ? '?activeOnly=true' : '';
    const res = await hubFetch(`/api/rag/category${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createRagCategory(dto: CreateRagCategoryDto, token?: string | null): Promise<RagCategory> {
    const res = await hubFetch('/api/rag/category', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateRagCategory(id: string, dto: UpdateRagCategoryDto, token?: string | null): Promise<RagCategory> {
    const res = await hubFetch(`/api/rag/category/${id}`, { method: 'PATCH', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteRagCategory(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/rag/category/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Reasoning Strategies
  async listReasoningStrategiesByBot(botId: string, system?: string, token?: string | null): Promise<ReasoningStrategy[]> {
    const qs = system ? `?system=${encodeURIComponent(system)}` : '';
    const res = await hubFetch(`/api/reasoning-strategies/bot/${encodeURIComponent(botId)}${qs}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createReasoningStrategy(dto: CreateReasoningStrategyDto, token?: string | null): Promise<ReasoningStrategy> {
    const res = await hubFetch('/api/reasoning-strategies', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateReasoningStrategy(id: string, dto: UpdateReasoningStrategyDto, token?: string | null): Promise<ReasoningStrategy> {
    const res = await hubFetch(`/api/reasoning-strategies/${id}`, { method: 'PUT', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteReasoningStrategy(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/reasoning-strategies/${id}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },

  // Message bundles (Hub)
  async listMessageBundles(query: MessageBundleListQuery, token?: string | null): Promise<MessageBundleListResponse> {
    const params = new URLSearchParams();
    if (query.system) params.set('system', query.system);
    if (query.botId) params.set('botId', query.botId);
    if (query.tenantId) params.set('tenantId', query.tenantId);
    if (query.channel) params.set('channel', query.channel);
    if (query.key) params.set('key', query.key);
    if (query.limit != null) params.set('limit', String(query.limit));
    if (query.offset != null) params.set('offset', String(query.offset));
    const qs = params.toString();
    const res = await hubFetch(`/api/message-bundles${qs ? `?${qs}` : ''}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async getMessageBundle(id: string, token?: string | null): Promise<MessageBundle> {
    const res = await hubFetch(`/api/message-bundles/${encodeURIComponent(id)}`, { method: 'GET' }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async createMessageBundle(dto: CreateMessageBundleDto, token?: string | null): Promise<MessageBundle> {
    const res = await hubFetch('/api/message-bundles', { method: 'POST', body: JSON.stringify(dto) }, token);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async updateMessageBundle(id: string, dto: UpdateMessageBundleDto, token?: string | null): Promise<MessageBundle> {
    const res = await hubFetch(
      `/api/message-bundles/${encodeURIComponent(id)}`,
      { method: 'PATCH', body: JSON.stringify(dto) },
      token,
    );
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },

  async deleteMessageBundle(id: string, token?: string | null): Promise<void> {
    const res = await hubFetch(`/api/message-bundles/${encodeURIComponent(id)}`, { method: 'DELETE' }, token);
    if (!res.ok) throw new Error(await res.text());
  },
};
